/**
 * SOCRATES criterion display names and the not-evaluated presentation
 * constants, shared by the item card and the Edit-item modal so the two
 * surfaces can't drift (ARC-206).
 *
 * BCE reports a criterion that could not be evaluated (e.g. criterion 5,
 * content_appropriate_stem, when the item has no source text) by setting its
 * error_flags value to NOT_EVALUATED (-1) instead of pass (0) / fail (1).
 * Recounting the map with those entries contributes to neither the passed
 * numerator nor the evaluated denominator (ARC-149). The display labels here
 * are the single source of truth for criterion names on every surface — do not
 * build a second lookup table.
 */

import { DONT_TEACH_KEY } from "@/utils/socratesDontTeach";

export const NOT_EVALUATED = -1;

export const CRITERIA_LABELS: Record<string, string> = {
  avoid_all_nothing: "Avoid All/Nothing",
  avoid_ambiguous_language: "Avoid Ambiguous Language",
  avoid_negatives: "Avoid Negatives",
  avoid_repeating_words: "Avoid Repeating Words",
  content_appropriate_stem: "Content-Appropriate Stem",
  do_not_teach_in_stem: "Don't Teach (in Stem or Options)",
  dont_clue_answer: "Don't Clue Answer",
  independent_mutually_exclusive: "Independent & Mutually Exclusive",
  one_correct_answer: "One Correct Answer",
  order_options_logically: "Order Options Logically",
  parallel_response_options: "Parallel Response Options",
  plausible_distractors: "Plausible Distractors",
  present_problem_in_stem: "Present Problem in Stem",
  similar_option_length: "Similar Option Length",
  simple_direct_concise: "Simple, Direct & Concise",
  structural_considerations: "Structural Considerations",
};

/**
 * Reason copy for a not-evaluated criterion. ARC-149's criterion-5 line names
 * what was missing rather than what is wrong. It is used verbatim as the
 * fallback; when the analyzer's response carries a not_evaluated map entry
 * (keyed by the same snake_case criterion codes as error_flags) that reason is
 * preferred. Neither the count string ("not evaluated") nor this line is to be
 * reworded here — Toni picks one term later (ARC-206 note, do not fix).
 */
export const CRITERION_5_NOTE = "not evaluable, no source text";

export interface NotEvaluatedCycleLike {
  /** The analyzer's per-criterion reasons for criteria it could not evaluate,
   *  keyed by the same snake_case codes as error_flags. */
  not_evaluated?: Record<string, string>;
}

/**
 * The reason line for a not-evaluated criterion. Prefers the reason the
 * analyzer carries in its `not_evaluated` map for that criterion, falling back
 * to ARC-149's established criterion-5 line when the analyzer attaches no
 * specific reason. Shared by the card and the Edit modal so the copy can't
 * drift.
 */
export function notEvaluatedReason(
  cycle: NotEvaluatedCycleLike | undefined | null,
  key: string,
): string {
  const reason = cycle?.not_evaluated?.[key];
  return reason && reason.trim().length > 0 ? reason : CRITERION_5_NOTE;
}

/**
 * The four states the criteria panel and its summary line can name (ARC-223),
 * in the order they always appear. A state is omitted from the summary when
 * its count is zero; the criteria grid marks any criterion in the judgment
 * state blue (its icon + label) instead of red.
 */
export type ItemQualityState =
  | "passed"
  | "failed"
  | "not_evaluated"
  | "judgment";

/** The summary-state display words — the single source of truth for the copy,
 *  never reworded per surface. */
export const ITEM_QUALITY_STATE_LABELS: Record<ItemQualityState, string> = {
  passed: "passed",
  failed: "failed",
  not_evaluated: "not evaluated",
  judgment: "needs your judgment",
};

/** The summary-state text colour, matching the same state's marker in the
 *  criteria grid: passed green, failed red, not evaluated grey, judgment blue
 *  (blue-600, #2563EB — ARC-223 Change 1). */
export function itemQualityStateClass(state: ItemQualityState): string {
  switch (state) {
    case "passed":
      return "text-green-600";
    case "failed":
      return "text-red-600";
    case "not_evaluated":
      return "text-muted-foreground";
    case "judgment":
      return "text-blue-600";
  }
}

export interface ItemQualitySegment {
  state: ItemQualityState;
  count: number;
}

/** The non-zero states for the summary line, in the fixed order passed, failed,
 *  not evaluated, needs your judgment. Zero-count states are dropped entirely
 *  (ARC-223 Change 3) — naming every state makes the old denominator
 *  unnecessary. */
export function itemQualitySegments(
  passed: number,
  failed: number,
  notEvaluated: number,
  judgment: number,
): ItemQualitySegment[] {
  const counts: Array<[ItemQualityState, number]> = [
    ["passed", passed],
    ["failed", failed],
    ["not_evaluated", notEvaluated],
    ["judgment", judgment],
  ];
  const out: ItemQualitySegment[] = [];
  for (const [state, count] of counts) {
    if (count > 0) out.push({ state, count });
  }
  return out;
}

/**
 * The criterion keys in the judgment state for a scored cycle — flagged
 * criteria (error_flags === 1) whose finding is a judgment call rather than a
 * defect. The generalised read (ARC-223 Change 2): any criterion whose
 * per-cycle quality_feedback entry carries severity=advisory is a judgment
 * call, so NEW judgment criteria work on severity alone. Don't Teach is the
 * temporary hardcoded fallback — its own severity is still "minor" (from BCE's
 * _generate_feedback), so the do_not_teach_in_stem key is treated as a
 * judgment call regardless of severity. When ARC-222 Step 5 switches Don't
 * Teach to `advisory`, THIS fallback is removed from the panel at the same
 * time (see the note filed against ARC-222).
 */
export function judgmentCriterionKeys(
  flags: Record<string, number>,
  feedback: ReadonlyArray<{ criterion?: string; severity?: string }>,
): Set<string> {
  const advisory = new Set<string>();
  for (const fb of feedback) {
    const crit = (fb.criterion ?? "").trim().toLowerCase();
    if (crit.length > 0 && crit !== "unknown" && (fb.severity ?? "") === "advisory") {
      advisory.add(crit);
    }
  }
  const keys = new Set<string>();
  for (const [key, value] of Object.entries(flags)) {
    if (value === 1 && (key === DONT_TEACH_KEY || advisory.has(key))) keys.add(key);
  }
  return keys;
}